import { type RefObject } from 'react';

import {
  type Table,
  type TableOptions,
  type TableState,
  type VisibilityState,
  getCoreRowModel,
} from '@tanstack/react-table';

import { useDataTable } from '@spotify-internal/data-table';

import {
  type DataTableSelection,
  applyDataTableSelectionChange,
  createDataTableSelectionOptions,
} from '../helpers/dataTableSelection';
import {
  type DataTableRowActivation,
  type RowActivationCallbacks,
  useDataTableRowActivation,
} from './useDataTableRowActivation';

type ResourceRow = { name: string; displayName: string };

export type ResourceDataTableOptions<TData extends ResourceRow> =
  RowActivationCallbacks<TData> &
    Pick<
      TableOptions<TData>,
      | 'columns'
      | 'data'
      | 'onSortingChange'
      | 'onColumnVisibilityChange'
      | 'onColumnSizingChange'
    > &
    Partial<
      Pick<
        TableState,
        | 'sorting'
        | 'columnVisibility'
        | 'columnSizing'
        | 'columnPinning'
        | 'rowSelection'
      >
    > & {
      externalScrollRef?: RefObject<HTMLElement>;
      initialColumnVisibility?: VisibilityState;
      selection?: DataTableSelection<TData>;
    };

export type ResourceDataTableInstance<TData> = Pick<
  DataTableRowActivation<TData>,
  'interactionProps'
> & { table: Table<TData> };

export const useResourceDataTable = <TData extends ResourceRow>({
  columns,
  data,
  externalScrollRef,
  selection,
  rowSelection,
  sorting,
  onSortingChange,
  columnPinning,
  columnVisibility,
  columnSizing,
  onColumnSizingChange,
  initialColumnVisibility,
  onColumnVisibilityChange,
  onRowActivate,
  onRowOpenInNewTab,
}: ResourceDataTableOptions<TData>): ResourceDataTableInstance<TData> => {
  const { rowHandlers, interactionProps } = useDataTableRowActivation<TData>({
    onRowActivate,
    onRowOpenInNewTab,
  });
  const {
    checkboxColumns,
    checkboxColumnIds,
    rowSelection: selectedRows,
    ...selectionOptions
  } = createDataTableSelectionOptions<TData>({
    selection,
    rowSelection,
  });
  const { table } = useDataTable<TData>({
    externalScrollRef,
    columns: [...checkboxColumns, ...columns],
    data,
    manualSorting: true,
    enableSorting: Boolean(sorting),
    initialState: { columnVisibility: initialColumnVisibility },
    state: {
      sorting: sorting ?? [],
      rowSelection: selectedRows,
      columnPinning: {
        left: [...checkboxColumnIds, ...(columnPinning?.left ?? [])],
        right: columnPinning?.right,
      },
      ...(columnVisibility ? { columnVisibility } : {}),
      ...(columnSizing ? { columnSizing } : {}),
    },
    ...(onColumnSizingChange ? { onColumnSizingChange } : {}),
    ...(onSortingChange ? { onSortingChange } : {}),
    ...(onColumnVisibilityChange ? { onColumnVisibilityChange } : {}),
    ...selectionOptions,
    onStateChange: updater =>
      applyDataTableSelectionChange(table, updater, selection),
    enableSortingRemoval: true,
    enableColumnPinning: true,
    enableColumnResizing: true,
    isDraggable: false,
    ...rowHandlers,
    getRowId: row => row.name,
    getRowLabel: row => row.original.displayName,
    getCoreRowModel: getCoreRowModel(),
  });
  return { table, interactionProps };
};
